import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, ShoppingCart } from "lucide-react";
import logo from "@/assets/logo.png";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    fetchCartCount();
    
    // Update cart count bij storage changes en custom cart events
    const handleStorageChange = () => {
      fetchCartCount();
    };
    
    const handleCartUpdate = () => {
      fetchCartCount();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const fetchCartCount = () => {
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const total = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
      setCartCount(total);
    } catch (error) {
      console.error("Error fetching cart count:", error);
      setCartCount(0);
    }
  };


  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center z-50">
          <img src={logo} alt="Bo Barbers" width="61" height="80" className="h-16 sm:h-20 w-auto invert" />
        </Link>
        
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 ml-auto">
          <Link 
            to="/" 
            className="header-menu text-foreground/70 hover:text-foreground transition-colors"
          >
            HOME
          </Link>
          <Link 
            to="/about" 
            className="header-menu text-foreground/70 hover:text-foreground transition-colors"
          >
            ABOUT
          </Link>
          <Link 
            to="/services" 
            className="header-menu text-foreground/70 hover:text-foreground transition-colors"
          >
            SERVICES
          </Link>
          <Link 
            to="/products" 
            className="header-menu text-foreground/70 hover:text-foreground transition-colors"
          >
            PRODUCTS
          </Link>
          <a 
            href="https://www.instagram.com/bobarbersutrecht/" 
            target="_blank"
            rel="noopener noreferrer"
            className="header-menu text-foreground/70 hover:text-foreground transition-colors"
          >
            INSTAGRAM
          </a>
          {cartCount > 0 && (
            <Link to="/cart" className="relative">
              <Button variant="outline" size="sm" className="rounded-full">
                <ShoppingCart className="w-4 h-4" />
                <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
                  {cartCount}
                </Badge>
              </Button>
            </Link>
          )}
          <Link to="/contact">
            <Button size="sm">
              CONTACT
            </Button>
          </Link>
        </nav>

        {/* Mobile: Cart + Hamburger */}
        <div className="flex items-center gap-2 md:hidden">
          {cartCount > 0 && (
            <Link to="/cart" className="relative">
              <Button variant="ghost" size="icon" aria-label="Winkelwagen">
                <ShoppingCart className="w-5 h-5 text-foreground" />
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                  {cartCount}
                </Badge>
              </Button>
            </Link>
          )}
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Toggle menu">
                <Menu className="w-6 h-6 text-foreground" />
              </Button>
            </SheetTrigger>
          <SheetContent side="right" className="w-full h-full p-0 flex flex-col bg-background">
            {/* Logo bovenaan */}
            <div className="pt-8 pb-6 border-b border-border">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex justify-center">
                <img src={logo} alt="Bo Barbers" width="73" height="96" className="h-24 w-auto invert" />
              </Link>
            </div>

            {/* Navigatie items gecentreerd */}
            <nav className="flex flex-col items-center justify-center flex-1 gap-6 px-8">
              <Link 
                to="/" 
                className="header-menu text-foreground text-xl font-medium hover:text-primary transition-colors w-full text-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                HOME
              </Link>
              <Link 
                to="/about" 
                className="header-menu text-foreground text-xl font-medium hover:text-primary transition-colors w-full text-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                ABOUT
              </Link>
              <Link 
                to="/services" 
                className="header-menu text-foreground text-xl font-medium hover:text-primary transition-colors w-full text-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                SERVICES
              </Link>
              <Link 
                to="/products" 
                className="header-menu text-foreground text-xl font-medium hover:text-primary transition-colors w-full text-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                PRODUCTS
              </Link>
              <a 
                href="https://www.instagram.com/bobarbersutrecht/" 
                target="_blank"
                rel="noopener noreferrer"
                className="header-menu text-foreground text-xl font-medium hover:text-primary transition-colors w-full text-center py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                INSTAGRAM
              </a>
              {/* Contact knop */}
              <div className="w-full mt-4">
                <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="w-full block">
                  <Button 
                    className="w-full py-6 text-lg font-semibold"
                    size="lg"
                  >
                    CONTACT
                  </Button>
                </Link>
              </div>

              {cartCount > 0 && (
                <Link to="/cart" onClick={() => setIsMenuOpen(false)} className="w-full">
                  <Button variant="outline" className="w-full relative py-6">
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    <span className="text-lg">Winkelwagen</span>
                    <Badge className="ml-2 h-6 w-6 flex items-center justify-center">
                      {cartCount}
                    </Badge>
                  </Button>
                </Link>
              )}
            </nav>

          </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
